import { type Market, VENUES } from "@trader/shared/MarketData"
import { DISPLAY_TIME_ZONE } from "../../ui/displayTime.ts"
import { formatDecimal, type Product, type Side } from "@trader/shared/Money"
import { BigDecimal } from "effect"

// Display formats for paper trading. Money arrives as BigDecimal and is shown
// through Intl.NumberFormat on its exact decimal text (ES2023 Intl formats
// strings without a float round trip), so ₹10,00,000.05 never becomes
// 1000000.0499999. Nothing here computes money; it only formats it.

type Decimal = BigDecimal.BigDecimal

/** Plain decimal text Intl accepts as an exact number. */
const NUMERIC_TEXT = /^-?\d+(\.\d+)?$/
const isNumericText = (text: string): text is `${number}` => NUMERIC_TEXT.test(text)

/** The exact text of a decimal, typed for Intl (formatDecimal never uses exponents). */
const exactText = (value: Decimal): `${number}` => {
  const text = formatDecimal(value)
  return isNumericText(text) ? text : "0"
}

/** Lakh grouping (10,00,000) for rupees, thousands (1,000,000) for everything else. */
export const localeFor = (currency: string): string => (currency === "INR" ? "en-IN" : "en-US")

/** Decimal places of a currency's minor unit (JPY and KRW have none). */
export const minorUnits = (currency: string): number => (currency === "JPY" || currency === "KRW" ? 0 : 2)

const formatters = new Map<string, Intl.NumberFormat>()
const cached = (key: string, make: () => Intl.NumberFormat): Intl.NumberFormat => {
  let format = formatters.get(key)
  if (format === undefined) {
    format = make()
    formatters.set(key, format)
  }
  return format
}

const moneyFormat = (currency: string, signed: boolean): Intl.NumberFormat =>
  cached(`money:${currency}:${signed}`, () => {
    const digits = minorUnits(currency)
    const options: Intl.NumberFormatOptions = {
      minimumFractionDigits: digits,
      maximumFractionDigits: digits,
      roundingMode: "halfExpand",
      ...(signed ? { signDisplay: "exceptZero" } : {})
    }
    try {
      return new Intl.NumberFormat(localeFor(currency), { style: "currency", currency, ...options })
    } catch {
      // Not an ISO currency Intl knows: plain digits.
      return new Intl.NumberFormat(localeFor(currency), options)
    }
  })

/** "₹10,00,000.00", "$1,234.50", "¥1,235": rounded half away from zero to the minor unit. */
export const formatMoney = (value: Decimal | null | undefined, currency: string): string =>
  value === null || value === undefined ? "—" : moneyFormat(currency, false).format(exactText(value))

/** "+₹1,234.50" / "-₹12.00" / "₹0.00", for P&L. */
export const formatSignedMoney = (value: Decimal | null | undefined, currency: string): string =>
  value === null || value === undefined ? "—" : moneyFormat(currency, true).format(exactText(value))

/** A price with the instrument's decimals, e.g. "2,580.15". */
export const formatPrice = (value: Decimal | null | undefined, precision: number, currency = "USD"): string =>
  value === null || value === undefined
    ? "—"
    : cached(`price:${currency}:${precision}`, () =>
      new Intl.NumberFormat(localeFor(currency), {
        minimumFractionDigits: precision,
        maximumFractionDigits: precision,
        roundingMode: "halfExpand"
      })
    ).format(exactText(value))

/** A quantity, e.g. "10" or "1,500" (fractions only when there are any). */
export const formatQty = (value: Decimal | null | undefined, currency = "USD"): string =>
  value === null || value === undefined
    ? "—"
    : cached(`qty:${currency}`, () => new Intl.NumberFormat(localeFor(currency), { maximumFractionDigits: 8 })).format(exactText(value))

/** The CSS class of an amount: "up" (green), "down" (red), or none for zero. */
export const toneOf = (value: Decimal | null | undefined): string =>
  value === null || value === undefined || BigDecimal.isZero(value) ? "" : BigDecimal.isNegative(value) ? "down" : "up"

const HUNDRED = BigDecimal.fromBigInt(100n)

/** `part` as a percent of `whole`, e.g. "+1.25%"; "—" when `whole` is zero. Exact until the 2-decimal rounding. */
export const formatPercentOf = (part: Decimal, whole: Decimal): string => {
  if (BigDecimal.isZero(whole)) return "—"
  const ratio = BigDecimal.round(BigDecimal.divideUnsafe(BigDecimal.multiply(part, HUNDRED), whole), {
    scale: 2,
    mode: "half-from-zero"
  })
  const format = cached(
    "percent",
    () => new Intl.NumberFormat("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2, signDisplay: "exceptZero" })
  )
  return `${format.format(exactText(ratio))}%`
}

// --- Products, sides, markets --------------------------------------------------------------

export { PRODUCTS_BY_MARKET } from "@trader/shared/Money"

const INDIAN_PRODUCT: Readonly<Record<Product, string>> = { Cash: "CNC", Intraday: "MIS", Carryforward: "NRML" }

/** Short label for tables: "CNC" / "MIS" / "NRML" in India, "Cash" elsewhere. */
export const productLabel = (product: Product, market: Market | undefined): string =>
  market === "IN" ? INDIAN_PRODUCT[product] : product

/** Long label for the ticket: "Cash (CNC)". */
export const productTitle = (product: Product, market: Market | undefined): string =>
  market === "IN" ? `${product} (${INDIAN_PRODUCT[product]})` : product

export const PRODUCT_HINTS: Readonly<Record<Product, string>> = {
  Cash: "Delivery: no leverage, and you can only sell what you hold.",
  Intraday: "Can sell short. Squared off automatically at the account's square-off time (15:15 on NSE).",
  Carryforward: "Can sell short. Held overnight (in India charged as delivery)."
}

export const sideLabel = (side: Side): string => (side === "Buy" ? "Buy" : "Sell")

/** The market of an instrument id "VENUE:SYMBOL"; undefined for an unknown venue. */
export const marketOf = (instrumentId: string): Market | undefined => {
  const venue = instrumentId.split(":")[0] ?? ""
  return Object.values(VENUES).find((v) => v.id === venue)?.market
}

/** "NSE:RELIANCE" → "RELIANCE". */
export const symbolOf = (instrumentId: string): string => instrumentId.slice(instrumentId.indexOf(":") + 1)

// --- Time ---------------------------------------------------------------------------------

/** The timezone an account's clock is shown in: its market's (INR → Asia/Kolkata). */
export const timeZoneForCurrency = (currency: string): string =>
  Object.values(VENUES).find((venue) => venue.currency === currency)?.timezone ?? "UTC"

const timeFormats = new Map<string, Intl.DateTimeFormat>()
const timeFormat = (key: string, options: Intl.DateTimeFormatOptions): Intl.DateTimeFormat => {
  let format = timeFormats.get(key)
  if (format === undefined) {
    format = new Intl.DateTimeFormat("en-GB", options)
    timeFormats.set(key, format)
  }
  return format
}

/** "02 Jan 2024, 15:30" in Indian time (DISPLAY_TIME_ZONE); the date only when `withTime` is false. */
export const formatTime = (epochSeconds: number | null | undefined, withTime = true): string =>
  epochSeconds === null || epochSeconds === undefined
    ? "—"
    : timeFormat(`time:${withTime}`, {
      timeZone: DISPLAY_TIME_ZONE,
      day: "2-digit",
      month: "short",
      year: "numeric",
      ...(withTime ? { hour: "2-digit", minute: "2-digit", hourCycle: "h23" } : {})
    }).format(epochSeconds * 1000)

/** "Tue, 02 Jan 2024, 15:30" for the replay clock, in Indian time. */
export const formatClock = (epochSeconds: number): string =>
  timeFormat("clock", {
    timeZone: DISPLAY_TIME_ZONE,
    weekday: "short",
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23"
  }).format(epochSeconds * 1000)

const partsOf = (epochSeconds: number, timeZone: string): Readonly<Record<string, number>> => {
  const parts: Record<string, number> = {}
  const format = timeFormat(`parts:${timeZone}`, {
    timeZone,
    hourCycle: "h23",
    year: "numeric",
    month: "numeric",
    day: "numeric",
    hour: "numeric",
    minute: "numeric",
    second: "numeric"
  })
  for (const part of format.formatToParts(epochSeconds * 1000)) {
    if (part.type !== "literal") parts[part.type] = Number(part.value)
  }
  return parts
}

/** The zone's offset from UTC in seconds at an instant (Intl supplies the rules, DST included). */
const utcOffsetSeconds = (epochSeconds: number, timeZone: string): number => {
  const p = partsOf(epochSeconds, timeZone)
  const asUtc = Date.UTC(p["year"] ?? 1970, (p["month"] ?? 1) - 1, p["day"] ?? 1, p["hour"] ?? 0, p["minute"] ?? 0, p["second"] ?? 0) / 1000
  return asUtc - Math.floor(epochSeconds)
}

/** The venue-local calendar date "YYYY-MM-DD" of an instant. */
export const localDateOf = (epochSeconds: number, timeZone: string): string =>
  new Date((epochSeconds + utcOffsetSeconds(epochSeconds, timeZone)) * 1000).toISOString().slice(0, 10)

/** The instant of local midnight on a date "YYYY-MM-DD" in a zone; null for a malformed date. */
export const localMidnight = (isoDate: string, timeZone: string): number | null => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(isoDate)) return null
  const asUtc = Date.parse(`${isoDate}T00:00:00Z`) / 1000
  if (!Number.isFinite(asUtc)) return null
  // Two passes settle the offset even on a daylight-saving change day.
  const first = asUtc - utcOffsetSeconds(asUtc, timeZone)
  return asUtc - utcOffsetSeconds(first, timeZone)
}
